/**
 * Single-Select-Combobox mit Live-Volltextsuche (2026-06-03, Joerg).
 *
 * Ersetzt klassische `<select>`-Elemente fuer Stamm-Daten (Kunde,
 * Mitarbeiter, Projekt etc.) — Anwender tippt, die Liste filtert
 * direkt und ein Vorschlag wird per Klick oder Enter uebernommen.
 *
 * Verhalten:
 *  - Input zeigt das aktuelle Label, sobald ein Wert gesetzt ist.
 *    Bei Fokus oder Tippen wird der Inhalt zur Suche.
 *  - Filter ist case-insensitive, sucht in `label` (substring) plus
 *    optional weitere Felder via `searchableText`-Override.
 *  - Tastatur: ArrowUp/ArrowDown navigiert, Enter waehlt, Escape
 *    schliesst und stellt das aktuelle Label wieder her.
 *  - Click outside schliesst und stellt das aktuelle Label wieder her.
 *  - `allowClear`: rendert einen X-Button rechts, der den Wert leert.
 *
 * Bewusst keine Server-Side-Suche — die Komponente arbeitet auf der
 * bereits geladenen `options`-Liste. Bei sehr grossen Listen (>500)
 * sollte die aufrufende Seite vorab filtern oder paginieren.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronDown } from 'lucide-react';

export interface SearchableSelectOption {
  id: string;
  label: string;
  /** Optional: zusaetzlicher Text fuer die Suche (z.B. Email, Kundennr). */
  searchableText?: string;
  /** Optional: gedaempfter Sekundaer-Text rechts in der Liste. */
  hint?: string;
}

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  /** Zeigt einen X-Button rechts, der den Wert auf null setzt. */
  allowClear?: boolean;
  disabled?: boolean;
  /** Wenn gesetzt: dieser Text wird angezeigt statt "Keine Treffer". */
  emptyHint?: string;
  /** Max sichtbare Vorschlaege (Default 50). */
  maxResults?: number;
  className?: string;
  id?: string;
  /** Vom <label htmlFor=...> referenziertes Element. */
  ariaLabel?: string;
}

export default function SearchableSelect({
  value, onChange, options,
  placeholder = 'Suchen...', allowClear = false, disabled = false,
  emptyHint, maxResults = 50,
  className = '', id, ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value],
  );

  // Wenn nicht offen: Input zeigt Label des aktuellen Werts. Im offenen
  // Zustand zeigt es die Query.
  const inputValue = open ? query : (selected?.label ?? '');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, maxResults);
    return options
      .filter((o) => {
        const hay = `${o.label} ${o.searchableText ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, maxResults);
  }, [options, query, maxResults]);

  // Bei Filter-Aenderungen Highlight zuruecksetzen.
  useEffect(() => { setHighlight(0); }, [query, open]);

  // Click outside schliesst und verwirft Query.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function commit(opt: SearchableSelectOption) {
    onChange(opt.id);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) commit(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          aria-label={ariaLabel}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          className="w-full px-3 py-2 pr-16 rounded bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 disabled:opacity-50"
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1 pointer-events-none text-slate-500">
          {allowClear && value && !disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(null); setQuery(''); }}
              className="pointer-events-auto p-1 rounded hover:bg-slate-700 text-slate-400"
              tabIndex={-1}
              title="Leeren"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={16} />
        </div>
      </div>

      {open && (
        <div className="absolute left-0 right-0 mt-1 z-20 max-h-60 overflow-y-auto rounded border border-slate-700 bg-slate-900 shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500 italic">
              {query.trim() ? (emptyHint ?? 'Keine Treffer') : 'Tippen zum Filtern...'}
            </div>
          ) : (
            <ul role="listbox">
              {filtered.map((opt, i) => {
                const active = i === highlight;
                const isSelected = opt.id === value;
                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setHighlight(i)}
                      onMouseDown={(e) => e.preventDefault() /* sonst loest blur vor click aus */}
                      onClick={() => commit(opt)}
                      className={
                        'w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ' +
                        (active ? 'bg-slate-700/60 text-slate-100' : 'text-slate-200 hover:bg-slate-800')
                      }
                    >
                      <span className={isSelected ? 'font-medium' : ''}>{opt.label}</span>
                      {opt.hint && <span className="text-xs text-slate-500 shrink-0">{opt.hint}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
